/**
 * „Neu in Vespera“ — die Karten für den Reiter „Neu“ in Zuhause. (13.10.2026, Bahn zuhause-neu)
 *
 * Reine Daten und reine Regeln, ohne Oberfläche. EINE Quelle: der Reiter (frontend/lib/neu-reiter.ts)
 * und die Tests lesen von hier.
 *
 * ## Woher der Inhalt kommt
 *
 * Nur Belegtes: jede Karte steht auf einer Zeile „Für Martin:“ im CHANGELOG oder auf dem Memory-Eintrag
 * zur Version (`seit`). Die Texte sagen nicht mehr, als dort steht. Was am iPhone noch nicht geprüft ist
 * (Morse-Ton), steht so im Text und wird nicht als fertig verkauft.
 *
 * ## Wer sieht was
 *
 *   - `alle`     jedes Konto
 *   - `konto1`   nur Konto 1 (dieselbe Regel wie `morseFuerKonto` und `warteSatzFuerKonto`; die Tests
 *                prüfen, dass sie gleich rechnen)
 *   - `ankermomente`  nur Konten mit der Freischaltung `features.ankermomente` (Tarif; der Menüpunkt hängt an derselben)
 *   - `ios`/`chrome`  für alle sichtbar, mit Schild, wer es braucht (Gerät, nicht Konto). Der Mac-Teil (Verbindungen →
 *                     Vespera Desktop, „Hier arbeiten“) existiert nur in Konto 1: die Mac-Karten sind `konto1`.
 *
 * ## Knöpfe nur mit Wirkung
 *
 * `ziel` ist eine echte Stelle der App (Einstellungsseite, Ansicht, Unterseite). Wo keine existiert, steht
 * KEIN `ziel` und die Karte hat keinen Knopf. `nurKonto1` am Ziel: die Stelle gibt es nur im Konto 1
 * (Verbindungen: Mac- und Chrome-Block), andere Konten bekommen die Karte ohne Knopf.
 */
import { MORSE_KONTO } from "./morse-konto.js";
import { istNeuerAls, istWebVersion } from "./web-version.js";

type NeuFuer = "alle" | "konto1" | "ios" | "mac" | "chrome" | "ankermomente";
export type NeuThema = "sprechen" | "alltag" | "morse" | "anrufe" | "mac" | "konto";

/** Wohin der Knopf führt. `knopf` ist das Wort darauf. */
export type NeuZiel =
  | { art: "einstellungen"; sub: string; knopf: string; nurKonto1?: boolean }
  | { art: "ansicht"; view: string; knopf: string; nurKonto1?: boolean }
  | { art: "seite"; key: string; knopf: string; nurKonto1?: boolean };

export interface NeuKarte {
  id: string;
  thema: NeuThema;
  titel: string;
  text: string;
  fuer: NeuFuer;
  /** Die Version, mit der es neu wurde, in der Schreibweise der App („8.0.0-pre.3652“). */
  seit: string;
  /** Zeichen aus lib/zeichen.ts. */
  zeichen: string;
  ziel?: NeuZiel;
}

/** Die Filterschilder, in der Reihenfolge des Entwurfs. */
export const NEU_THEMEN: ReadonlyArray<{ id: NeuThema; name: string }> = [
  { id: "sprechen", name: "Sprechen" },
  { id: "alltag", name: "Alltag" },
  { id: "morse", name: "Morse" },
  { id: "anrufe", name: "Anrufe" },
  { id: "mac", name: "Mac" },
  { id: "konto", name: "Konto" },
];

/** Das Schild „für wen“ auf der Karte. */
export const NEU_FUER_TEXT: Record<NeuFuer, string> = {
  alle: "Alle Konten",
  konto1: "Nur Konto 1",
  ios: "iPhone-App",
  mac: "Mac-App",
  chrome: "Chrome-Erweiterung",
  ankermomente: "Mit Ankermomenten",
};

/** Die Freischaltungen des Kontos, wie /api/auth/me sie liefert (nur was hier gebraucht wird). */
export type NeuFeatures = { ankermomente?: boolean } | null | undefined;

/** Wo die zuletzt angesehene Version steht (localStorage) — je Konto, denn der Browser kann mehrere Konten tragen. */
const NEU_GESEHEN_SCHLUESSEL = "voice-v8.neuigkeiten-karten.gesehen";
export function neuGesehenSchluessel(userId: number | null | undefined): string {
  return `${NEU_GESEHEN_SCHLUESSEL}.${userId ?? "ohne"}`;
}

const v = (n: number): string => `8.0.0-pre.${n}`;

/** Die Karten, wichtigste zuerst. Die erste ist die große Hervorhebung. */
export const NEU_KARTEN: readonly NeuKarte[] = [
  {
    id: "morse",
    thema: "morse",
    titel: "Morse lernen",
    text:
      "Hören, selbst geben und die Buchstaben laut sagen. Eine Tabelle zeigt alle Zeichen zum Antippen, " +
      "und wenn ein Zeichen zu viel ist, schaltest du eine Stufe zurück. Am iPhone ist der Ton noch nicht geprüft.",
    fuer: "konto1",
    seit: v(3652),
    zeichen: "klang",
    ziel: { art: "ansicht", view: "morse", knopf: "Morse öffnen" },
  },
  {
    id: "anruf",
    thema: "anrufe",
    titel: "Vespera ruft dich an",
    text:
      "Sag „ruf mich an“ oder „in 10 Minuten“, und es klingelt wie ein echter Anruf. Einschalten, Ruhezeit und " +
      "Anrufe pro Tag stellst du in den Einstellungen unter Mitteilungen ein. Es braucht die neue Version der iPhone-App.",
    fuer: "konto1",
    seit: v(3666),
    zeichen: "erinnerung",
    ziel: { art: "einstellungen", sub: "mitteilungen", knopf: "Einstellungen öffnen" },
  },
  {
    id: "abschicken",
    thema: "sprechen",
    titel: "Schneller abgeschickt",
    text:
      "Die Sprechtaste entscheidet selbst, wann dein Satz zu Ende ist, und schickt ihn dann ab. Weniger Warten nach dem Satz. " +
      "Die Einstellungen dazu stehen unter Sprechtaste und „Hey Vespera“.",
    fuer: "alle",
    seit: v(3684),
    zeichen: "mikro",
    ziel: { art: "einstellungen", sub: "sprechen", knopf: "Einstellungen öffnen" },
  },
  {
    id: "anker",
    thema: "alltag",
    titel: "Ankermomente",
    text: "Eine Seite mit allen Ankern und ihren Vorschaubildern. Du findest sie im Menü unter Mehr.",
    fuer: "ankermomente",
    seit: v(3473),
    zeichen: "stern",
    ziel: { art: "seite", key: "anker", knopf: "Ankermomente ansehen" },
  },
  {
    id: "mac",
    thema: "mac",
    titel: "Vespera am Mac",
    text:
      "Mit der Mac-App liest Vespera Dateien aus Downloads, Schreibtisch und Dokumenten, schaut in Kalender, " +
      "Erinnerungen und Notizen und hilft in Blender. Mehrere Macs können gleichzeitig verbunden sein; welcher arbeitet, wählst du mit „Hier arbeiten“.",
    fuer: "konto1",
    seit: v(3698),
    zeichen: "dokument",
    ziel: { art: "einstellungen", sub: "verbindungen", knopf: "Verbindungen öffnen", nurKonto1: true },
  },
  {
    id: "schluessel",
    thema: "konto",
    titel: "Eigener Schlüssel",
    text:
      "Du kannst deinen eigenen Anthropic-Schlüssel eintragen und siehst Verbrauch heute und in sieben Tagen " +
      "samt Tageslimit. Ob du das Abo oder deinen Schlüssel nutzt, legt der Admin fest. Trag den Schlüssel nur dort ein, nie in den Chat.",
    fuer: "alle",
    seit: v(3738),
    zeichen: "person",
    ziel: { art: "einstellungen", sub: "modelle", knopf: "Modelle öffnen" },
  },
  {
    id: "glocke",
    thema: "konto",
    titel: "Ruhige Glocke",
    text: "Die Meldungen stehen in Gruppen, und die Glocke zeigt nur, was dich braucht oder fertig ist. „Alles gelesen“ geht mit Rückgängig.",
    fuer: "alle",
    seit: v(3691),
    zeichen: "erinnerung",
  },
  {
    id: "floskeln",
    thema: "sprechen",
    titel: "Gleich zur Sache",
    text:
      "Vespera fängt Antworten nicht mehr mit „Moment, ich prüfe das“ an und sagt nichts mehr zu deinem Schimpfen: " +
      "keine Rüge, kein „bleib ruhig“.",
    fuer: "alle",
    seit: v(3737),
    zeichen: "gespraech",
  },
  {
    id: "diktat",
    thema: "sprechen",
    titel: "Langes Diktat geht nicht verloren",
    text:
      "Was du Vespera Langes diktierst, ist sofort gespeichert. Klappt die Antwort nicht, sagt sie dir, dass dein Text " +
      "gespeichert ist und du ihn nicht noch einmal sprechen musst.",
    fuer: "alle",
    seit: v(3725),
    zeichen: "aufzeichnen",
  },
  {
    id: "warte-satz",
    thema: "sprechen",
    titel: "Wartezeit-Satz auf Wunsch",
    text:
      "Auf Wunsch sagt Vespera einmal „Einen Moment.“, wenn sie nach dem Loslassen länger still bleibt. " +
      "Er ist ausgeschaltet; einschalten kannst du ihn in den Einstellungen unter Sprechtaste.",
    fuer: "konto1",
    seit: v(3737),
    zeichen: "laden",
    ziel: { art: "einstellungen", sub: "sprechen", knopf: "Einstellungen öffnen" },
  },
  {
    id: "stimmen",
    thema: "alltag",
    titel: "Unsichere Stimmen",
    text:
      "Ist eine Stimme nicht sicher erkannt, schreibt Vespera „jemand, vielleicht …“ statt einen Namen als Tatsache. " +
      "Pläne und Termine anderer Personen landen nicht mehr in deinen To-dos.",
    fuer: "alle",
    seit: v(3733),
    zeichen: "person",
  },
  {
    id: "blender",
    thema: "mac",
    titel: "Blender ohne Einzelfreigabe",
    text:
      "In den Einstellungen unter „Vespera Desktop (Mac)“ tippst du einmal, dann arbeitet Blender eine Stunde lang ohne Rückfrage. " +
      "Nach gelesenem Webtext oder einer Mail fragt Blender weiter nach, das ist Absicht.",
    fuer: "konto1",
    seit: v(3735),
    zeichen: "plus",
    ziel: { art: "einstellungen", sub: "verbindungen", knopf: "Verbindungen öffnen", nurKonto1: true },
  },
  {
    id: "chrome",
    thema: "mac",
    titel: "Chrome-Erweiterung",
    text: "Du kannst die Verbindung sofort trennen, und beim Ausfüllen im Browser meldet Vespera nur, was sie wirklich eingetragen hat.",
    fuer: "konto1",
    seit: v(3676),
    zeichen: "haken",
    ziel: { art: "einstellungen", sub: "verbindungen", knopf: "Verbindungen öffnen", nurKonto1: true },
  },
];

/** Ist das Konto 1? (Dieselbe Regel wie morseFuerKonto / warteSatzFuerKonto.) Rein. */
export function istKonto1(userId: number | null | undefined): boolean {
  return userId === MORSE_KONTO;
}

/** Die Karten, die dieses Konto sieht. Rein. */
export function kartenFuerKonto(
  userId: number | null | undefined,
  features?: NeuFeatures,
  karten: readonly NeuKarte[] = NEU_KARTEN,
): NeuKarte[] {
  return karten.filter(
    (k) => (k.fuer !== "konto1" || istKonto1(userId)) && (k.fuer !== "ankermomente" || features?.ankermomente === true),
  );
}

/** Die Schilder, die etwas filtern: nur Themen, zu denen es für dieses Konto Karten gibt. Rein. */
export function themenMitKarten(karten: readonly NeuKarte[]): Array<{ id: NeuThema; name: string }> {
  return NEU_THEMEN.filter((t) => karten.some((k) => k.thema === t.id)).map((t) => ({ ...t }));
}

/** Filtern: `"alle"` lässt alles durch. Rein. */
export function filtere(karten: readonly NeuKarte[], thema: NeuThema | "alle"): NeuKarte[] {
  return thema === "alle" ? [...karten] : karten.filter((k) => k.thema === thema);
}

/** Die Karten, die seit dem Merker dazugekommen sind. Ein fehlender oder unlesbarer Merker: alle. Rein. */
export function ungesehene(karten: readonly NeuKarte[], gesehen: string | null | undefined): NeuKarte[] {
  return istWebVersion(gesehen) ? karten.filter((k) => istNeuerAls(k.seit, gesehen)) : [...karten];
}

/** Die neueste Version unter den Karten (das, was beim Ansehen gemerkt wird); leer ohne Karten. Rein. */
export function neuesteSeit(karten: readonly NeuKarte[]): string {
  return karten.reduce((a, k) => (a === "" || istNeuerAls(k.seit, a) ? k.seit : a), "");
}

/** Das Ziel, das dieses Konto wirklich erreicht — sonst null (dann: kein Knopf). Rein. */
export function zielFuerKonto(karte: NeuKarte, userId: number | null | undefined): NeuZiel | null {
  if (!karte.ziel) return null;
  if (karte.ziel.nurKonto1 && !istKonto1(userId)) return null;
  return karte.ziel;
}

/** Der Satz unter dem Titel. Rein. */
export function neuSatz(anzahl: number): string {
  if (anzahl <= 0) return "Nichts Neues seit deinem letzten Besuch. Das sind die letzten Neuerungen.";
  return anzahl === 1 ? "Eine Neuerung seit deinem letzten Besuch" : `${anzahl} Neuerungen seit deinem letzten Besuch`;
}
